import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { useTimezoneSelect } from "react-timezone-select";
import { useWebsiteButton } from "../../customeHooks/useWebsiteButtons";
import { SingleFindEvent } from "../../redux/features/event/event.types";
import {
  currencyShorts,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
  tzOptions,
} from "../../utils/constants";
import { formatDate, formatToCurrency } from "../../utils/helpers";
import { Icon } from "../icon/icon";
import { BgWrapper } from "../wrapper/bgWrapper";
import { EventImage } from "./eventImage";

export const FindEventCard = ({ event }: { event: SingleFindEvent }) => {
  const { parseTimezone } = useTimezoneSelect(tzOptions);

  const { goToEvent } = useWebsiteButton();

  const isFreeEvent = (event?.price ?? 0) <= 0;

  return (
    <Link
      className="flex w-full flex-col gap-4 overflow-hidden rounded-2xl md:flex-row"
      to={goToEvent(event?.slug!)}
    >
      <EventImage image={event.image} dimension={200} className="" />

      <div className="flex flex-1 flex-col gap-1">
        <BgWrapper className="max-w-fit font-fraunces text-xl leading-[100%] tracking-[-0.04em]">
          {event.name}
        </BgWrapper>

        <BgWrapper className="flex max-w-fit items-center gap-2 text-sm font-bold">
          <Icon
            svg="map-pin"
            width={20}
            height={20}
            className="text-grey-500"
          />
          <p className="line-clamp-1">
            {event.hideLocation
              ? (event.placeholderLocation ?? "")
              : event.location}
          </p>
        </BgWrapper>

        <BgWrapper className="flex max-w-fit items-center gap-2 text-sm font-bold">
          <Icon
            svg="calendar"
            width={20}
            height={20}
            className="text-grey-500"
          />
          {formatDate(event?.startDate, "ddd, MMMM DD, YYYY", true)} •{" "}
          {event?.startTime ?? ""}{" "}
          {event.timezone ? `(${parseTimezone(event.timezone)?.abbrev})` : ""}
        </BgWrapper>

        <BgWrapper className="flex max-w-fit items-center gap-2 text-sm font-bold text-blue-500">
          <Icon
            svg={isFreeEvent ? "sparkle" : "hand-coins"}
            width={20}
            height={20}
          />
          <p className="line-clamp-2">
            {isFreeEvent
              ? "FREE"
              : formatToCurrency(event?.price ?? 0, {
                  currency: currencyShorts[event?.currency ?? "NGN"],
                })}
          </p>
        </BgWrapper>
      </div>
    </Link>
  );
};

export const FindEventCardLoader = () => {
  return (
    <div className="flex w-full flex-col gap-4 overflow-hidden rounded-2xl md:flex-row">
      <div className="w-full md:max-w-[200px]">
        <Skeleton
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          baseColor={SKELETON_BASE_COLOR}
          width={"100%"}
          height={200}
          className="rounded-lg"
        />
      </div>

      <div className="flex flex-1 flex-col gap-1">
        <Skeleton
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          baseColor={SKELETON_BASE_COLOR}
          width={"100%"}
          height={40}
          className="rounded-xl"
        />

        <Skeleton
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          baseColor={SKELETON_BASE_COLOR}
          width={"70%"}
          height={48}
          className="rounded-xl"
        />
        <Skeleton
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          baseColor={SKELETON_BASE_COLOR}
          width={"50%"}
          height={48}
          className="rounded-xl"
        />

        <Skeleton
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          baseColor={SKELETON_BASE_COLOR}
          width={"30%"}
          height={48}
          className="rounded-xl"
        />
      </div>
    </div>
  );
};
